import { Trans, useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Anonymous, SparkleStar } from '@peacecover/icon';
import { useGetBettieBalances } from '../../../domains/bettie/ui/hooks/useGetBettieBalances';

interface OpenNextPreviewCardProps {
  remainingPreviewsCount: number | null;
  speaker: string | null;
  onClick: () => void;
  disabled: boolean;
  isThread?: boolean;
  isLoading?: boolean;
  backgroundColor?: string;
  isGenerating?: boolean;
}

const OpenNextPreviewCard = ({
  remainingPreviewsCount,
  speaker,
  onClick,
  disabled,
  isThread = true,
  isLoading = false,
  backgroundColor = '#4B34B8',
  isGenerating = false
}: OpenNextPreviewCardProps) => {
  const { t } = useTranslation();
  return isThread ? (
    <div className="text-pure-white px-[22px] py-[15px] flex items-start gap-[10px]">
      <div
        className="rounded-full w-[26px] h-[26px] flex items-center justify-center relative flex-shrink-0"
        style={{ backgroundColor }}
      >
        <Anonymous size={22} className="text-white absolute left-[1.6px] top-[1.6px]" />
      </div>
      <div className="flex flex-col items-start gap-[5px] w-full">
        <div className="text-[14px] font-semibold leading-[17px] text-separator-light-1">
          {speaker ?? t('ScenePreviewDetail.anonymous')}
        </div>
        <OpenNextPreviewCardContent
          remainingPreviewsCount={remainingPreviewsCount}
          onClick={onClick}
          disabled={disabled}
          isLoading={isLoading}
          isThread={isThread}
          isGenerating={isGenerating}
        />
      </div>
    </div>
  ) : (
    <div className="px-[22px] pt-[8px]">
      <OpenNextPreviewCardContent
        remainingPreviewsCount={remainingPreviewsCount}
        onClick={onClick}
        disabled={disabled}
        isLoading={isLoading}
        isThread={isThread}
        isGenerating={isGenerating}
      />
    </div>
  );
};

export default OpenNextPreviewCard;

const OpenNextPreviewCardContent = ({
  remainingPreviewsCount,
  onClick,
  disabled,
  isLoading = false,
  isThread = true,
  isGenerating = false
}: {
  remainingPreviewsCount: number | null;
  onClick: () => void;
  disabled: boolean;
  isLoading?: boolean;
  isThread?: boolean;
  isGenerating?: boolean;
}) => {
  const { t } = useTranslation();
  const { data: bettieBalances } = useGetBettieBalances();
  const currentHearts = bettieBalances?.bettiePoint?.balance ?? 0;

  const fadeVariants = {
    initial: { opacity: 0, y: 5 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -5 }
  };

  const type = isThread ? t('ScenePreviewDetail.thread') : t('ScenePreviewDetail.chat');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`relative rounded-[16px] w-full p-[1px] overflow-hidden ${isLoading ? 'shadow-[1px_-1px_12px_0_rgba(92,71,246,0.30),-1px_1px_12px_0_rgba(72,206,181,0.30)]' : ''}`}
    >
      <div
        className={`pointer-events-none absolute inset-0 rounded-[16px] bg-graident-multi-blue ${isGenerating ? 'opacity-30' : ''}`}
        aria-hidden
      />
      <div className="relative z-[1] p-[16px] bg-black rounded-[16px] flex items-center justify-between w-full min-h-[70px]">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loading-state"
              variants={fadeVariants}
              initial={false}
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2 }}
              className="flex flex-col items-start w-full gap-[8px]"
            >
              <div className="skeleton-shimmer bg-[linear-gradient(90deg,rgba(255,255,255,0.20)_0%,rgba(255,255,255,0.00)_130.46%)] w-full max-w-[185px] h-[16px] rounded-[12px] mt-[2px]" />
              <div className="relative w-full">
                <div className="skeleton-shimmer bg-[linear-gradient(90deg,rgba(255,255,255,0.20)_0.01%,rgba(255,255,255,0.00)_58.74%,rgba(255,255,255,0.10)_111.06%)] w-full max-w-[253px] h-[17px] rounded-[12px]" />
                <div className={`absolute top-[18px] ${isThread ? 'right-[-5px]' : 'right-0'}`}>
                  <SparkleStar
                    size={11}
                    gradientStops={[
                      { offset: '-11%', color: '#45E0AD' },
                      { offset: '100%', color: '#5C3DFA' }
                    ]}
                    className="absolute bottom-0 left-0"
                  />
                  <SparkleStar
                    size={6}
                    gradientStops={[
                      { offset: '-11%', color: '#45E0AD' },
                      { offset: '100%', color: '#5C3DFA' }
                    ]}
                    className="absolute top-0 right-0"
                  />
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="content-state"
              variants={fadeVariants}
              initial={false}
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2 }}
              className="flex flex-col items-start w-full gap-[8px]"
            >
              <div className="flex items-center gap-[6px]">
                <div className="px-[4px] py-[2px] bg-[rgba(255,255,255,0.12)] rounded-[4px] text-[11px] font-medium leading-[13px] text-[#CFCFD0]">
                  {t('ScenePreviewDetail.leftPreview', {
                    type,
                    count: remainingPreviewsCount ?? 0
                  })}
                </div>
                <p className="text-[11px] font-medium leading-[13px] text-main-dark-2">
                  {t('ScenePreviewDetail.currentHearts', { count: currentHearts })}
                </p>
              </div>
              <div className="flex items-center gap-[5px]">
                <img
                  src="/assets/icons/graphic-3d/3D.like.png"
                  alt="Like"
                  className="w-[16px] h-[16px]"
                />
                <p className="text-[15px] font-bold leading-[18px] text-main-dark-1">
                  <Trans
                    i18nKey="ScenePreviewDetail.openNextPreview"
                    values={{ type, count: 1 }}
                    components={{
                      span: (
                        <span className="bg-graident-multi-blue bg-clip-text text-transparent" />
                      )
                    }}
                  />
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {isGenerating ? (
          <div className="px-[7px] py-[4px] flex-shrink-0 bg-secondary-dark rounded-[30px] flex items-center gap-[4px]">
            <div className="relative w-[10px] h-[10px]">
              <SparkleStar
                size={7}
                gradientStops={[
                  { offset: '0%', color: '#45E0AD' },
                  { offset: '100%', color: '#5C3DFA' }
                ]}
                className="absolute top-[2px] left-[1px]"
              />
              <SparkleStar
                size={3}
                gradientStops={[
                  { offset: '0%', color: '#45E0AD' },
                  { offset: '100%', color: '#5C3DFA' }
                ]}
                className="absolute top-[1px] right-[1px]"
              />
            </div>
            <span className="text-onSecondary-dark text-[11px] font-semibold leading-[13px]">
              {t('ScenePreviewDetail.isCreating')}
            </span>
          </div>
        ) : (
          <span
            className={`px-[7px] py-[4px] flex-shrink-0 bg-pure-white text-primary-light text-[11px] font-semibold leading-[13px] rounded-[30px] ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          >
            {t('ScenePreviewDetail.open')}
          </span>
        )}
      </div>
    </button>
  );
};
